import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSort,
  faSortUp,
  faSortDown,
  faArrowUp,
  faArrowDown,
  faChartLine,
  faEye,
  faFilter,
  faTimes,
  faSearch,
  faSortAmountDown,
  faSortAmountUp
} from '@fortawesome/free-solid-svg-icons';
import { faAws, faGoogle, faGithub } from '@fortawesome/free-brands-svg-icons';
import { CostData, ServiceCostBreakdown } from '../types';

interface AccountCostTableProps {
  data: CostData;
  loading?: boolean;
  maxItems?: number;
}

type SortField = 'name' | 'cost' | 'trend' | 'cloud';
type SortDirection = 'asc' | 'desc';

interface TableRow {
  id: string;
  name: string;
  cloud: 'aws' | 'gcp' | 'github';
  cost: number;
  trend: {
    direction: 'up' | 'down' | 'stable';
    percentage: number;
  };
  services: string[];
  region?: string;
}

const AccountCostTableSkeleton: React.FC<{ rows: number }> = ({ rows }) => (
  <div className="bg-white border border-gray-300 rounded overflow-hidden animate-pulse font-mono">
    <div className="px-3 py-2 bg-gray-100 border-b border-gray-300">
      <div className="h-3 bg-gray-200 rounded w-24"></div>
    </div>
    <div className="divide-y divide-gray-200">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="px-3 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 bg-gray-200 rounded"></div>
              <div>
                <div className="h-3 bg-gray-200 rounded w-20 mb-1"></div>
                <div className="h-2 bg-gray-200 rounded w-12"></div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="h-3 bg-gray-200 rounded w-16"></div>
              <div className="h-3 bg-gray-200 rounded w-12"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const AccountCostTable: React.FC<AccountCostTableProps> = ({
  data,
  loading = false,
  maxItems = 10
}) => {
  const [sortField, setSortField] = useState<SortField>('cost');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [cloudFilter, setCloudFilter] = useState<string>('all');
  const [selectedAccount, setSelectedAccount] = useState<{ name: string; cloud: string; cost: number; services: ServiceCostBreakdown[] } | null>(null);
  const [detailSearch, setDetailSearch] = useState('');
  const [detailSortAsc, setDetailSortAsc] = useState(false);

  // Transform data into table rows
  const tableRows: TableRow[] = useMemo(() => {
    const rows: TableRow[] = [];

    // Add AWS accounts
    data.clouds.aws.accounts.forEach(account => {
      rows.push({
        id: `aws-${account.accountId}`,
        name: account.accountName,
        cloud: 'aws',
        cost: account.totalCost,
        trend: account.trend,
        services: account.services.map(s => s.serviceName),
        region: 'Multiple'
      });
    });

    // Add GCP projects
    data.clouds.gcp.projects.forEach(project => {
      rows.push({
        id: `gcp-${project.projectId}`,
        name: project.projectName,
        cloud: 'gcp',
        cost: project.totalCost,
        trend: project.trend,
        services: project.services.map(s => s.serviceName),
        region: 'Multiple'
      });
    });

    // Add GitHub orgs
    data.clouds.github.orgs.forEach(org => {
      rows.push({
        id: `github-${org.orgName}`,
        name: org.orgName,
        cloud: 'github',
        cost: org.totalCost,
        trend: org.trend,
        services: org.usage.map(u => u.type),
        region: 'Global'
      });
    });

    return rows;
  }, [data]);

  // Filter and sort rows
  const processedRows = useMemo(() => {
    let filtered = tableRows;

    // Apply cloud filter
    if (cloudFilter !== 'all') {
      filtered = filtered.filter(row => row.cloud === cloudFilter);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'cost':
          comparison = a.cost - b.cost;
          break;
        case 'trend':
          comparison = a.trend.percentage - b.trend.percentage;
          break;
        case 'cloud':
          comparison = a.cloud.localeCompare(b.cloud);
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    // Limit results
    return filtered.slice(0, maxItems);
  }, [tableRows, sortField, sortDirection, cloudFilter, maxItems]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return faSort;
    return sortDirection === 'asc' ? faSortUp : faSortDown;
  };

  const getCloudIcon = (cloud: 'aws' | 'gcp' | 'github') => {
    switch (cloud) {
      case 'aws': return faAws;
      case 'gcp': return faGoogle;
      case 'github': return faGithub;
    }
  };

  const getCloudColor = (cloud: 'aws' | 'gcp' | 'github') => {
    switch (cloud) {
      case 'aws': return 'text-orange-700 bg-orange-100';
      case 'gcp': return 'text-blue-700 bg-blue-100';
      case 'github': return 'text-gray-700 bg-gray-100';
    }
  };

  const getTrendIcon = (direction: 'up' | 'down' | 'stable') => {
    switch (direction) {
      case 'up': return faArrowUp;
      case 'down': return faArrowDown;
      default: return faChartLine;
    }
  };

  const getTrendColor = (direction: 'up' | 'down' | 'stable') => {
    switch (direction) {
      case 'up': return 'text-red-600';
      case 'down': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  if (loading) {
    return <AccountCostTableSkeleton rows={maxItems} />;
  }

  return (
    <div className="space-y-3 font-mono">
      {/* Header with Filter */}
      <div className="bg-white border border-gray-300 rounded p-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-900">Account Cost Breakdown</h3>

          {/* Cloud Filter */}
          <div className="flex items-center space-x-2">
            <FontAwesomeIcon icon={faFilter} className="text-gray-400 text-xs" />
            <select
              value={cloudFilter}
              onChange={(e) => setCloudFilter(e.target.value)}
              className="text-xs border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Clouds</option>
              <option value="aws">AWS Only</option>
              <option value="gcp">GCP Only</option>
              <option value="github">GitHub Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-300 rounded overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100 border-b border-gray-300">
              <tr>
                <th
                  className="px-3 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
                  onClick={() => handleSort('cloud')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Cloud</span>
                    <FontAwesomeIcon icon={getSortIcon('cloud')} className="text-xs" />
                  </div>
                </th>
                <th
                  className="px-3 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Account/Project</span>
                    <FontAwesomeIcon icon={getSortIcon('name')} className="text-xs" />
                  </div>
                </th>
                <th
                  className="px-3 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
                  onClick={() => handleSort('cost')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Monthly Cost</span>
                    <FontAwesomeIcon icon={getSortIcon('cost')} className="text-xs" />
                  </div>
                </th>
                <th
                  className="px-3 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
                  onClick={() => handleSort('trend')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Trend</span>
                    <FontAwesomeIcon icon={getSortIcon('trend')} className="text-xs" />
                  </div>
                </th>
                <th className="px-3 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Services
                </th>
                <th className="px-3 py-2 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {processedRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-4 text-center text-xs text-gray-500">
                    {cloudFilter === 'all'
                      ? 'No account data available'
                      : `No ${cloudFilter.toUpperCase()} accounts found`
                    }
                  </td>
                </tr>
              ) : (
                processedRows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <div className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getCloudColor(row.cloud)}`}>
                        <FontAwesomeIcon icon={getCloudIcon(row.cloud)} className="mr-1 text-xs" />
                        {row.cloud.toUpperCase()}
                      </div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="text-sm font-bold text-gray-900">{row.name}</div>
                      <div className="text-xs text-gray-500">{row.region}</div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="text-lg font-bold text-gray-900">
                        ${row.cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <div className="text-xs text-gray-500">
                        {((row.cost / data.summary.totalSpend) * 100).toFixed(1)}% of total
                      </div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className={`flex items-center ${getTrendColor(row.trend.direction)}`}>
                        <FontAwesomeIcon icon={getTrendIcon(row.trend.direction)} className="mr-1 text-sm" />
                        <span className="text-sm font-bold">
                          {row.trend.percentage.toFixed(1)}%
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">vs last month</div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="text-sm text-gray-900">
                        {row.services.slice(0, 2).join(', ')}
                        {row.services.length > 2 && (
                          <span className="text-gray-500">
                            {' '}+{row.services.length - 2} more
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        className="text-blue-600 hover:text-blue-900 transition-colors"
                        onClick={() => {
                          // Find the account's full service data
                          let services: ServiceCostBreakdown[] = [];
                          if (row.cloud === 'aws') {
                            const account = data.clouds.aws.accounts.find(a => a.accountId === row.id.replace('aws-', ''));
                            services = account?.services || [];
                          } else if (row.cloud === 'gcp') {
                            const project = data.clouds.gcp.projects.find(p => p.projectId === row.id.replace('gcp-', ''));
                            services = project?.services || [];
                          } else if (row.cloud === 'github') {
                            const org = data.clouds.github.orgs.find(o => o.orgName === row.id.replace('github-', ''));
                            services = (org?.usage || []).map(u => ({
                              serviceName: `GitHub ${u.type.charAt(0).toUpperCase() + u.type.slice(1)}`,
                              totalCost: u.cost,
                              percentage: 0,
                              trend: { direction: 'stable' as const, percentage: 0, period: 'monthly' as const }
                            }));
                          }
                          setSelectedAccount({ name: row.name, cloud: row.cloud, cost: row.cost, services });
                          setDetailSearch('');
                        }}
                      >
                        <FontAwesomeIcon icon={faEye} className="mr-1 text-xs" />
                        View Details
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Table Footer */}
        {processedRows.length > 0 && (
          <div className="bg-gray-50 px-3 py-2 border-t border-gray-300">
            <div className="flex items-center justify-between text-sm text-gray-600">
              <div>
                Showing {processedRows.length} of {tableRows.length} accounts
                {cloudFilter !== 'all' && ` (filtered by ${cloudFilter.toUpperCase()})`}
              </div>
              <div className="font-bold">
                Total: ${processedRows.reduce((sum, row) => sum + row.cost, 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Account Detail Modal */}
      {selectedAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 font-mono" onClick={() => setSelectedAccount(null)}>
          <div className="bg-white rounded shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className={`sticky top-0 px-4 py-3 flex items-center justify-between rounded-t ${
              selectedAccount.cloud === 'aws' ? 'bg-orange-500' : selectedAccount.cloud === 'gcp' ? 'bg-blue-500' : 'bg-gray-700'
            }`}>
              <div className="text-white">
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={selectedAccount.cloud === 'aws' ? faAws : selectedAccount.cloud === 'gcp' ? faGoogle : faGithub} className="text-lg" />
                  <h2 className="text-base font-bold">{selectedAccount.name}</h2>
                </div>
                <p className="text-xs mt-0.5 opacity-80">
                  {selectedAccount.services.length} services — Total: ${selectedAccount.cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <button onClick={() => setSelectedAccount(null)} className="text-white hover:opacity-80">
                <FontAwesomeIcon icon={faTimes} className="text-lg" />
              </button>
            </div>

            {/* Search & Sort */}
            <div className="px-4 py-2 border-b border-gray-200 flex items-center gap-2">
              <div className="relative flex-1">
                <FontAwesomeIcon icon={faSearch} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                <input
                  type="text"
                  placeholder="Search services..."
                  value={detailSearch}
                  onChange={e => setDetailSearch(e.target.value)}
                  className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:border-blue-400"
                />
              </div>
              <button
                onClick={() => setDetailSortAsc(!detailSortAsc)}
                className="px-2 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1"
              >
                <FontAwesomeIcon icon={detailSortAsc ? faSortAmountUp : faSortAmountDown} />
                {detailSortAsc ? 'Low to High' : 'High to Low'}
              </button>
            </div>

            {/* Services List */}
            <div className="overflow-y-auto max-h-[calc(90vh-160px)]">
              {selectedAccount.services.length === 0 ? (
                <div className="p-8 text-center text-xs text-gray-500">No services found for this account</div>
              ) : (
                <table className="w-full">
                  <thead className="sticky top-0 bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left text-xs font-semibold text-gray-600 px-4 py-2">Service</th>
                      <th className="text-right text-xs font-semibold text-gray-600 px-4 py-2">Cost</th>
                      <th className="text-right text-xs font-semibold text-gray-600 px-4 py-2 w-32">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const filtered = selectedAccount.services.filter(s =>
                        s.serviceName.toLowerCase().includes(detailSearch.toLowerCase())
                      );
                      const sorted = [...filtered].sort((a, b) =>
                        detailSortAsc ? a.totalCost - b.totalCost : b.totalCost - a.totalCost
                      );
                      const maxCost = sorted.length > 0 ? Math.max(...sorted.map(s => s.totalCost)) : 1;

                      return sorted.map((svc, idx) => (
                        <tr key={`${svc.serviceName}-${idx}`} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-2 text-xs font-medium text-gray-900">{svc.serviceName}</td>
                          <td className="px-4 py-2 text-xs font-bold text-gray-900 text-right">
                            ${svc.totalCost >= 1 ? svc.totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : svc.totalCost.toFixed(4)}
                          </td>
                          <td className="px-4 py-2 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <div className="w-16 bg-gray-200 rounded-full h-1.5">
                                <div
                                  className={`h-1.5 rounded-full ${selectedAccount.cloud === 'aws' ? 'bg-orange-500' : selectedAccount.cloud === 'gcp' ? 'bg-blue-500' : 'bg-gray-600'}`}
                                  style={{ width: `${Math.max(1, (svc.totalCost / maxCost) * 100)}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-500 w-12 text-right">{svc.percentage.toFixed(1)}%</span>
                            </div>
                          </td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              )}
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-4 py-2 flex items-center justify-between">
              <span className="text-xs text-gray-500">{selectedAccount.services.length} services</span>
              <button
                onClick={() => setSelectedAccount(null)}
                className="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-100 font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};